import { ReplaySubject } from "rxjs";

export class SelectionWrapper<T extends { id?: string | number }> {
  private _selection: Array<{ element: HTMLElement; id: string | number }> = [];
  private _sourceGetterFn: () => T[];
  private _sourceGetterFn2: () => T[];

  public selectionChanged = new ReplaySubject<T[]>();

  constructor(sourceGetterFn: () => T[], sourceGetterFn2?: () => T[]) {
    this._sourceGetterFn = sourceGetterFn;
    this._sourceGetterFn2 = sourceGetterFn2 ?? (() => []);
  }

  public get Items() {
    return this._selection
      ? this._selection.map((x) => this._FindInSource(x.id)).filter((x) => x)
      : [];
  }

  public get Count() {
    return this._selection.length;
  }

  public IsItemSelected(item: T) {
    return this._FindItemIndex(item) >= 0;
  }

  public Reset() {
    for (let i = this._selection.length - 1; i >= 0; --i) {
      this._DeselectItem(i);
    }
    this.selectionChanged.next(this.Items);
  }

  private _FindInSource(id: string | number): T {
    if (!this._sourceGetterFn) {
      return null;
    }
    const items = [...this._sourceGetterFn(), ...this._sourceGetterFn2()];
    return items.find((x) => x.id === id) ?? null;
  }

  public SelectItem(item: T) {
    this._SelectItem(item, null);
    this.selectionChanged.next(this.Items);
  }
  public DeselectItem(item: T) {
    this._DeselectItem(this._FindItemIndex(item));
    this.selectionChanged.next(this.Items);
  }

  public HandleItem(item: T, ev: MouseEvent) {
    if (!item || !ev) {
      return;
    }
    const element = ev.currentTarget as HTMLElement;
    if (!element) {
      return;
    }
    const isSelected = this.IsItemSelected(item);

    const mouseBtnRight = 2;

    let deselectAll = false;
    if (this._selection.length > 0 && !ev.ctrlKey) {
      if (
        (ev.type !== "dragstart" || (ev.type === "dragstart" && !isSelected)) &&
        (ev.button !== mouseBtnRight || !isSelected)
      ) {
        deselectAll = true;
      }
    }
    if (deselectAll) {
      for (let i = this._selection.length - 1; i >= 0; --i) {
        this._DeselectItem(i);
      }
    }
    if (
      ev.ctrlKey &&
      ev.type === "mouseup" &&
      isSelected &&
      ev.button !== mouseBtnRight
    ) {
      this._DeselectItem(this._FindItemIndex(item));
    } else {
      this._SelectItem(item, element);
    }
    this.selectionChanged.next(this.Items);
  }

  private _SelectItem(item: T, element: HTMLElement) {
    if (this.IsItemSelected(item)) {
      return;
    }
    if (element) {
      element.setAttribute("aria-grabbed", "true");
      element.classList.add("selected");
    }
    this._selection.push({
      element,
      id: item.id,
    });
  }

  private _DeselectItem(index: number) {
    if (index < 0 || index >= this._selection.length) {
      return;
    }
    const element = this._selection.splice(index, 1)[0].element;
    if (element != null) {
      element.setAttribute("aria-grabbed", "false");
      element.classList.remove("selected");
    }
  }

  private _FindItemIndex(item: T) {
    return this._selection.findIndex((x) => x.id === item.id);
  }
}
